/**
 * routes/Stats — the learning analytics view (TF.5). Reads /api/stats (the setup_stats
 * buckets + agent_scorecards) and turns "how are we actually doing, and where" into
 * charts:
 *
 *  - a KPI row of the pooled figures (samples, winrate, avg realized R, buckets tracked);
 *  - a winrate / avg-R breakdown by a switchable dimension (setup · regime · funding ·
 *    leverage), so an operator can see e.g. that range-fades in a trend bleed;
 *  - the MAE/MFE excursion distribution per setup; and
 *  - the committee scorecards — which role's call matches the outcome how often.
 *
 * History is thin early on, so every panel has an honest empty state: n=0 shows "no data
 * yet", never a fabricated bar. Charts follow the dataviz skill (see DimBar /
 * ExcursionChart). All aggregation here is pure and derived from the setup_stats rows.
 */
import { useMemo, useState } from 'react';

import { api } from '../api/client';
import type { AgentScorecard, SetupStat, StatsResponse } from '../api/types';
import { usePoll } from '../api/usePoll';
import { Card } from '../components/Card';
import { StatTile } from '../components/StatTile';
import { DimBar } from '../components/charts/DimBar';
import type { DimDatum, DimMode } from '../components/charts/DimBar';
import { ExcursionChart } from '../components/charts/ExcursionChart';
import type { ExcursionRow } from '../components/charts/ExcursionChart';
import { ago, num, pctPlain } from '../lib/format';

// ── dimension + metric switches ───────────────────────────────────────────────
type DimKey = 'setupType' | 'marketRegime' | 'fundingBucket' | 'leverageBucket';

const DIMENSIONS: readonly { key: DimKey; label: string }[] = [
  { key: 'setupType', label: 'Setup' },
  { key: 'marketRegime', label: 'Regime' },
  { key: 'fundingBucket', label: 'Funding' },
  { key: 'leverageBucket', label: 'Leverage' },
];

const METRICS: readonly { key: DimMode; label: string }[] = [
  { key: 'winrate', label: 'Winrate' },
  { key: 'avgR', label: 'Avg R' },
];

const ROLE_LABEL: Record<string, string> = {
  signal: 'Signal', bear: 'Bear-case', risk: 'Risk',
  news: 'News', historian: 'Historian', arbiter: 'Arbiter',
};

/** Prettify a bucket/enum value for display (trend-pullback → Trend pullback, 3-5x → 3–5×). */
function pretty(value: string): string {
  if (/x$/.test(value)) return value.replace(/x$/, '×').replace('>', '>');
  const s = value.replace(/[-_]/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** n-weighted mean of a nullable per-bucket figure; null when no bucket carries it. */
function weightedMean(rows: readonly SetupStat[], pick: (r: SetupStat) => number | null): number | null {
  let sum = 0;
  let weight = 0;
  for (const r of rows) {
    const v = pick(r);
    if (v == null) continue;
    sum += v * r.n;
    weight += r.n;
  }
  return weight === 0 ? null : sum / weight;
}

/** Aggregate the buckets by one dimension into winrate/avgR bars, busiest first. */
function breakdown(stats: readonly SetupStat[], dim: DimKey, mode: DimMode): DimDatum[] {
  const groups = new Map<string, SetupStat[]>();
  for (const s of stats) {
    const key = s[dim];
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(s);
  }
  const out: DimDatum[] = [];
  for (const [key, rows] of groups) {
    const n = rows.reduce((a, r) => a + r.n, 0);
    if (n === 0) continue;
    const value =
      mode === 'winrate'
        ? rows.reduce((a, r) => a + r.wins, 0) / n
        : weightedMean(rows, (r) => r.avgRrRealized) ?? 0;
    out.push({ key, label: pretty(key), value, n });
  }
  return out.sort((a, b) => b.n - a.n);
}

/** Per-setup excursion rows (n-weighted p50/p90), only where both sides have data. */
function excursions(stats: readonly SetupStat[]): ExcursionRow[] {
  const groups = new Map<string, SetupStat[]>();
  for (const s of stats) (groups.get(s.setupType) ?? groups.set(s.setupType, []).get(s.setupType)!).push(s);
  const out: ExcursionRow[] = [];
  for (const [key, rows] of groups) {
    const n = rows.reduce((a, r) => a + r.n, 0);
    const maeP50 = weightedMean(rows, (r) => r.maeP50);
    const maeP90 = weightedMean(rows, (r) => r.maeP90);
    const mfeP50 = weightedMean(rows, (r) => r.mfeP50);
    const mfeP90 = weightedMean(rows, (r) => r.mfeP90);
    if (maeP50 == null || maeP90 == null || mfeP50 == null || mfeP90 == null) continue;
    out.push({ key, label: pretty(key), n, maeP50, maeP90, mfeP50, mfeP90 });
  }
  return out.sort((a, b) => b.n - a.n);
}

export function Stats(): JSX.Element {
  const stats = usePoll<StatsResponse>((signal) => api.stats(signal), { intervalMs: 30_000 });
  const [dim, setDim] = useState<DimKey>('setupType');
  const [metric, setMetric] = useState<DimMode>('winrate');

  const rows = stats.data?.setupStats ?? [];
  const cards = stats.data?.scorecards ?? [];
  const seed = stats.data?.seed === true;

  const pooled = useMemo(() => {
    const n = rows.reduce((a, r) => a + r.n, 0);
    const wins = rows.reduce((a, r) => a + r.wins, 0);
    return {
      n,
      wins,
      winrate: n ? wins / n : null,
      avgR: weightedMean(rows, (r) => r.avgRrRealized),
      buckets: rows.length,
    };
  }, [rows]);

  const bars = useMemo(() => breakdown(rows, dim, metric), [rows, dim, metric]);
  const excRows = useMemo(() => excursions(rows), [rows]);

  const loading = stats.loading && !stats.data;
  const empty = !loading && rows.length === 0 && cards.length === 0;

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Statistics</h1>
        <span className="page-head__sub">
          {stats.updatedAt ? `updated ${ago(stats.updatedAt)}` : 'loading…'}
        </span>
        {seed && <span className="demo-flag" title="Figures below are seeded fixture data, not live results.">Seed data</span>}
      </div>

      {stats.error && !stats.data && (
        <div className="banner-error section" role="alert">
          Learning stats unavailable: {stats.error}
        </div>
      )}

      {loading ? (
        <div className="state"><span className="spinner" aria-hidden /><span>Loading statistics…</span></div>
      ) : empty ? (
        <Card title="Learning aggregates">
          <div className="state">
            <span className="muted">
              No closed-trade history yet. Winrate, R and the committee scorecards populate here
              once the engine has taken and closed trades — nothing is shown until then.
            </span>
          </div>
        </Card>
      ) : (
        <>
          {/* ── pooled KPIs ─────────────────────────────────────────────────── */}
          <div className="stat-grid section">
            <StatTile
              label="Samples"
              value={pooled.n ? num(pooled.n, 0) : '—'}
              sub={`${pooled.buckets} bucket${pooled.buckets === 1 ? '' : 's'} tracked`}
              tone="neutral"
            />
            <StatTile
              label="Winrate"
              value={pooled.winrate != null ? pctPlain(pooled.winrate, 1) : '—'}
              sub={pooled.n ? `${pooled.wins}W · ${pooled.n - pooled.wins}L` : 'no trades'}
              tone={pooled.winrate != null && pooled.winrate >= 0.5 ? 'profit' : 'neutral'}
            />
            <StatTile
              label="Avg R"
              value={pooled.avgR != null ? `${pooled.avgR >= 0 ? '+' : '−'}${Math.abs(pooled.avgR).toFixed(2)}R` : '—'}
              sub="realized, per trade"
              tone={pooled.avgR != null ? (pooled.avgR >= 0 ? 'profit' : 'loss') : 'neutral'}
            />
            <StatTile
              label="Setups"
              value={new Set(rows.map((r) => r.setupType)).size || '—'}
              sub="distinct setup types"
              tone="neutral"
            />
          </div>

          {/* ── winrate / avg-R by dimension ────────────────────────────────── */}
          <Card
            title={metric === 'winrate' ? 'Winrate by bucket' : 'Avg realized R by bucket'}
            hint={`grouped by ${DIMENSIONS.find((d) => d.key === dim)!.label.toLowerCase()}`}
            actions={
              <div className="seg" role="group" aria-label="Metric">
                {METRICS.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    className={`seg__btn${metric === m.key ? ' is-active' : ''}`}
                    aria-pressed={metric === m.key}
                    onClick={() => setMetric(m.key)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            }
          >
            <div className="filters filters--flush" role="group" aria-label="Breakdown dimension">
              <div className="filters__group">
                <span className="filters__label">By</span>
                <div className="seg" role="group" aria-label="Break down by">
                  {DIMENSIONS.map((d) => (
                    <button
                      key={d.key}
                      type="button"
                      className={`seg__btn${dim === d.key ? ' is-active' : ''}`}
                      aria-pressed={dim === d.key}
                      onClick={() => setDim(d.key)}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DimBar data={bars} mode={metric} />
          </Card>

          {/* ── excursions + scorecards ─────────────────────────────────────── */}
          <div className="grid-2 section">
            <Card title="Excursions by setup" hint="MAE / MFE in R">
              <ExcursionChart rows={excRows} />
            </Card>

            <Card title="Committee scorecards" hint={`${cards.length} role${cards.length === 1 ? '' : 's'}`} flush>
              <Scorecards cards={cards} />
            </Card>
          </div>
        </>
      )}
    </>
  );
}

/** The per-role precision/recall table — precision/recall as inline sequential meters. */
function Scorecards({ cards }: { readonly cards: readonly AgentScorecard[] }): JSX.Element {
  if (cards.length === 0) {
    return <div className="state"><span className="muted">No committee scorecards yet.</span></div>;
  }
  const ordered = [...cards].sort((a, b) => (b.precision ?? -1) - (a.precision ?? -1));
  return (
    <div className="table-wrap">
      <table className="data-table scorecard-table">
        <thead>
          <tr>
            <th>Role</th>
            <th className="align-right">n</th>
            <th>Precision</th>
            <th>Recall</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((c) => (
            <tr key={c.role}>
              <td>
                <strong>{ROLE_LABEL[c.role] ?? pretty(c.role)}</strong>
                <div className="muted scorecard-conf num">
                  {c.truePositive}TP · {c.falsePositive}FP · {c.trueNegative}TN · {c.falseNegative}FN
                </div>
              </td>
              <td className="align-right num">{c.n}</td>
              <td><ScoreMeter value={c.precision} /></td>
              <td><ScoreMeter value={c.recall} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A thin sequential meter for a 0..1 rate; honest dash when the denominator is empty. */
function ScoreMeter({ value }: { readonly value: number | null }): JSX.Element {
  if (value == null) return <span className="muted num">—</span>;
  return (
    <div className="score" title={pctPlain(value, 1)}>
      <div className="score__track">
        <span className="score__fill" style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }} />
      </div>
      <span className="score__val num">{pctPlain(value, 0)}</span>
    </div>
  );
}
